import { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { getOrganizationId, getSaaId } from "../../../../utils/session.utils";
import { getMinimumSale, exportMinimumSale } from "../../../../api/gamma/index";
import { getImputation } from "../../../../api/accounting/imputation";
import { getDrawSettlementDate } from "../../../../api/gamma";
import FileSaver from "file-saver";

/**
 * Hook personalizado para manejar lógica relacionada con la venta mínima.
 * @returns {Object} Retorna un objeto con estados y funciones relacionados con la venta mínima.
 */
const useMinimumSale = () => {
  /**
   * Selector para obtener la agencia seleccionada del estado global de Redux.
   * Se utiliza para obtener el número de agencia necesario para la consulta de venta mínima.
   * @returns {Object} Retorna un objeto con la agencia seleccionada, que incluye su número y nivel.
   */
  const { agencySelected } = useSelector((state) => ({
    agencySelected: state.myAgencies?.selected_agencie_to_consult,
  }));

  // Estados para manejar imputaciones, fechas de liquidación de sorteos, venta mínima, y estados de carga.
  const [imputations, setImputations] = useState([]);
  const [drawSettlementDate, setDrawSettlementDate] = useState(null);
  const [drawSettlementRaw, setDrawSettlementRaw] = useState([]); //agregado
  const [imputationsRaw, setImputationsRaw] = useState([]); //agregado
  const [minimumSale, setMinimumSale] = useState(null);

  // Estados UI para manejar la selección de fecha e imputación, y el estado de carga.
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedImputation, setSelectedImputation] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  // ========================== fetch data section ==========================

  /**
   * Función para obtener la venta mínima según la fecha de imputación y el ID de imputación.
   * @param {string} imputationDate - La fecha de imputación en formato "YYYY-MM-DD".
   * @param {number} imputationId - El ID de la imputación.
   * @returns {Promise<void>} - No retorna nada, pero actualiza el estado de minimumSale.
   * @throws {Error} - Lanza un error si la solicitud falla.
   */
  const fetchMinimumSale = useCallback(
    async (imputationDate, imputationId) => {
      setLoading(true);
      setError(null);
      try {
        const organizationId = getOrganizationId();
        const response = await getMinimumSale(
          organizationId,
          imputationDate,
          imputationId,
          agencySelected?.number,
        );
        setMinimumSale(response || []);
      } catch (error) {
        setMinimumSale([]); // Limpia la venta mínima en caso de error
        setError(
          "No se pudo obtener la venta mínima. Por favor, intenta nuevamente.",
        );
      } finally {
        setLoading(false);
      }
    },
    [agencySelected],
  );

  /**
   * Función para exportar la venta mínima en formato PDF o Excel según la fecha de imputación y el ID de imputación.
   * @param {string} format - El formato de exportación, puede ser "pdf" o "excel".
   * @param {string} imputationDate - La fecha de imputación en formato "YYYY-MM-DD".
   * @param {number} imputationId - El ID de la imputación.
   * @param {number} agencyNumber - El número de agencia (opcional, solo para PDF).
   * @returns {Promise<void>} - No retorna nada, pero inicia la descarga del archivo exportado.
   * @throws {Error} - Lanza un error si la solicitud falla.
   */
  const exportMinimumSaleData = useCallback(
    async (format, imputationDate, imputationId, agencyNumber) => {
      setExporting(true);
      try {
        const date = new Date();
        const datenow = date.toISOString().split("T")[0];
        const organizationId = getOrganizationId();
        const response = await exportMinimumSale(
          format,
          organizationId,
          imputationDate,
          imputationId,
          agencyNumber,
        );
        FileSaver.saveAs(
          response,
          `minimum_sale_${imputationDate}-${datenow}.pdf`,
        );
      } catch (error) {
        console.log("Error exporting minimum sale data:", error);
      } finally {
        setExporting(false);
      }
    },
    [],
  );

  /**
   * Función para obtener las imputaciones disponibles.
   * @returns {Promise<void>} - No retorna nada, pero actualiza el estado de imputations con las imputaciones obtenidas.
   * @throws {Error} - Lanza un error si la solicitud falla.
   */
  const fetchImputations = useCallback(async () => {
    try {
      const response = await getImputation();
      setImputationsRaw(response?.content || []); // Guardo data cruda para filtrar según fecha seleccionada
      const mappedImputations = mapImputationsToSelectOptions(
        response?.content || [],
      ); // Mapeo las imputaciones al formato necesario para el select
      setImputations(mappedImputations); // Actualizo el estado con las imputaciones mapeadas para mostrar en el select
    } catch (error) {
      console.log("Error fetching imputations:", error);
    }
  }, []);

  /**
   * Función para obtener la fecha de liquidación de sorteos.
   * @returns {Promise<void>} - Obtiene fechas de liquidación de sorteos.
   * @throws {Error} - Lanza un error si la solicitud falla.
   */
  const fetchDrawSettlementDate = useCallback(async () => {
    try {
      const response = await getDrawSettlementDate();
      const data = response.data;
      setDrawSettlementRaw(data); // Guardo data cruda x las dudas
      const mappedDates = mapDrawSettlementDatesToSelectOptions(data);
      setDrawSettlementDate(mappedDates);
    } catch (error) {
      console.log("Error fetching draw settlement dates:", error);
    }
  }, []);

  // ========================== helper functions section ==========================
  /**
   * Función para filtrar imputaciones disponibles según la fecha de imputación seleccionada.
   * Si no hay fecha seleccionada, muestra todas las imputaciones.
   * Si hay una fecha seleccionada, muestra solo las imputaciones que están asociadas a
   * esa fecha según el campo gameId de drawSettlementRaw.
   * @returns {Array} - Retorna un array de imputaciones filtradas para mostrar en el select de imputaciones.
   */
  const filteredImputations = useMemo(() => {
    if (!selectedDate) return imputations;

    const selected = drawSettlementRaw.find(
      (item) => item.imputationDate === selectedDate,
    ); // obtiene objeto fecha real

    if (!selected) return [];

    return mapImputationsToSelectOptions(
      imputationsRaw.filter((imp) => selected.gameId.includes(imp.sourceId)),
    );
  }, [selectedDate, drawSettlementRaw, imputationsRaw]);

  /**
   * Función para filtrar fechas de liquidación de sorteos según la imputación seleccionada.
   * Si no hay imputación seleccionada, muestra todas las fechas. Si hay una imputación seleccionada,
   * muestra solo las fechas que incluyen esa imputación en su gameId.
   * @returns {Array} - Retorna un array de fechas filtradas para mostrar en el select de fechas.
   */
  const filteredDates = useMemo(() => {
    if (!selectedImputation) return drawSettlementDate;
    // Busca fechas donde el gameId incluya la imputación seleccionada
    return drawSettlementRaw
      .filter((item) => item.gameId.includes(selectedImputation))
      .map((date) => ({
        id: date.imputationDate, //Number(Date.parse(date.imputationDate)),
        label: date.imputationDate,
      }));
  }, [selectedImputation, drawSettlementRaw, drawSettlementDate]);

  /**
   * Función para limpiar los filtros de fecha de imputación e imputación seleccionados.
   * Resetea ambos estados a null, lo que hace que se muestren todas las opciones en ambos selects.
   * @returns {void} - No retorna nada, pero actualiza los estados selectedDate y selectedImputation a null.
   */
  const clearFilters = () => {
    setSelectedDate(null);
    setSelectedImputation(null);
  };

  // ========================== useEffect section ==========================
  /**
   * useEffect para cargar las imputaciones y las fechas de liquidación de sorteos al montar el componente.
   * Llama a las funciones fetchImputations y fetchDrawSettlementDate para obtener los datos necesarios para los selects.
   * Este efecto se ejecuta solo una vez al montar el componente, ya que no tiene dependencias.
   * @returns {void} - No retorna nada, pero carga las imputaciones y fechas de liquidación de sorteos en el estado.
   */
  useEffect(() => {
    fetchImputations();
    fetchDrawSettlementDate();
  }, []);

  return {
    loading,
    imputations,
    drawSettlementDate,
    minimumSale,
    fetchMinimumSale,
    fetchImputations,
    fetchDrawSettlementDate,
    selectedDate,
    setSelectedDate,
    selectedImputation,
    setSelectedImputation,
    agencySelected,
    filteredImputations,
    filteredDates,
    clearFilters,
    error,
    exportMinimumSaleData,
    exporting,
  };
};

/**
 * Función para mapear las imputaciones obtenidas de la API a un formato adecuado para mostrar en el select de imputaciones. Toma el array de imputaciones y lo transforma en un array de objetos con id y label, donde id es el ID de la imputación y label es el nombre de la imputación.
 * @param {Array} imputations
 * @returns {Array} - Retorna un array de opciones para el select de imputaciones. Cada opción es un objeto con id y label, donde id es el ID de la imputación y label es el nombre de la imputación.
 */
export const mapImputationsToSelectOptions = (imputations) => {
  return imputations?.map((imp) => ({
    id: imp.sourceId,
    label: imp.name,
  }));
};

/**
 * Función para mapear las fechas de liquidación de sorteos obtenidas de la API a un formato adecuado para mostrar en el select de fechas. Toma el array de fechas y lo transforma en un array de objetos con id y label, donde id es la fecha de imputación y label es también la fecha de imputación (en formato "YYYY-MM-DD").
 * @param {Array} dates
 * @returns {Array} - Retorna un array de opciones para el select de fecha de imputaciones. Cada opción es un objeto con id y label, donde id es la fecha de imputación y label es también la fecha de imputación.
 */
export const mapDrawSettlementDatesToSelectOptions = (dates) => {
  return dates?.map((date) => ({
    id: date.imputationDate, //Number(Date.parse(date.imputationDate)),
    label: date.imputationDate,
  }));
};

export default useMinimumSale;
